/**
 * LabSheetPage — iter1053
 * „Моят лист за изследване": персонализирани правила за вземане,
 * какво да спра преди това, ден от цикъла и липсващите маркери.
 * Печат през браузъра (без външна библиотека).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, Loader2 } from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LabSheetPage() {
  const { isDark } = useTheme();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/endocrine/lab-sheet`, { timeout: 25000 })
      .then(({ data }) => setD(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const shell = isDark ? "bg-[hsl(222,47%,6%)] text-white" : "bg-[#F8FAFC] text-[#1A1A2E]";

  return (
    <div className={`min-h-screen ${shell}`} data-testid="lab-sheet-page">
      <SEO title="Моят лист за изследване"
        description="Персонализиран лист: кога и как се вземат Вашите хормони, какво да спрете преди това и какво липсва."
        path="/dashboard/lab-sheet" />
      <style>{`@media print {
        .no-print { display: none !important; }
        body { background: #fff !important; color: #000 !important; }
        .print-sheet { box-shadow: none !important; border: none !important; background: #fff !important; color: #000 !important; }
      }`}</style>

      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="no-print flex items-center justify-between">
          <Link to="/dashboard/hormonal-twin" className="inline-flex items-center gap-1 text-xs font-bold opacity-70 hover:opacity-100"
                data-testid="lab-sheet-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Към хормоналния близнак
          </Link>
          <button onClick={() => window.print()} data-testid="lab-sheet-print"
                  className="px-3 py-2 rounded-lg bg-violet-600 text-white text-xs font-bold flex items-center gap-1.5 hover:bg-violet-700">
            <Printer className="w-3.5 h-3.5" /> Печат / PDF
          </button>
        </div>

        {loading && <Loader2 className="w-5 h-5 animate-spin text-violet-500 mt-6" />}

        {d && (
          <div className={`print-sheet mt-4 p-5 rounded-2xl border ${
            isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"
          }`}>
            <h1 className="text-xl font-extrabold">{d.title_bg}</h1>
            <p className="text-[11px] opacity-75 mt-1">{d.intro_bg}</p>
            <div className="text-[10px] opacity-60 mt-1">
              Изготвен: {String(d.generated_at).slice(0, 10)}
              {d.cycle_day ? ` · ден от цикъла: ${d.cycle_day}` : ""}
            </div>

            {(d.stop_before || []).length > 0 && (
              <section className="mt-4" data-testid="lab-sheet-stop">
                <h2 className="text-sm font-bold">1. Какво да спра преди изследването</h2>
                <ul className="mt-1 space-y-1">
                  {d.stop_before.map((s) => (
                    <li key={s} className="text-[11px]">• {s}</li>
                  ))}
                </ul>
              </section>
            )}

            <section className="mt-4" data-testid="lab-sheet-timing">
              <h2 className="text-sm font-bold">2. Кога се вземат моите хормони</h2>
              <ul className="mt-1 space-y-1">
                {(d.timing || []).map((t) => (
                  <li key={t.title_bg} className="text-[11px]">
                    • <strong>{t.title_bg}</strong> — {t.when_bg}
                  </li>
                ))}
              </ul>
            </section>

            {(d.personal || []).length > 0 && (
              <section className="mt-4" data-testid="lab-sheet-personal">
                <h2 className="text-sm font-bold">3. Отнася се точно за мен</h2>
                <ul className="mt-1 space-y-1">
                  {d.personal.map((p) => (
                    <li key={p.title_bg} className="text-[11px]">
                      • <strong>{p.title_bg}</strong> — {p.personal_bg}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {(d.markers || []).length > 0 && (
              <section className="mt-4" data-testid="lab-sheet-markers">
                <h2 className="text-sm font-bold">4. Маркери, които липсват в профила ми</h2>
                <ul className="mt-1 space-y-1">
                  {d.markers.map((m) => (
                    <li key={m.label_bg} className="text-[11px]">
                      • <strong>{m.label_bg}</strong>
                      <span className="opacity-70"> ({m.axis_bg})</span> — {m.why_bg}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="mt-4" data-testid="lab-sheet-general">
              <h2 className="text-sm font-bold">5. Общи правила</h2>
              <ul className="mt-1 space-y-1">
                {(d.general || []).map((g) => (
                  <li key={g} className="text-[11px]">• {g}</li>
                ))}
              </ul>
            </section>

            <p className="text-[10px] italic opacity-70 mt-4" data-testid="lab-sheet-disclaimer">
              {d.disclaimer_bg}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
